import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import App from './App';
import { NotificationProvider } from './contexts/NotificationContext';
import vsaasTheme from './theme';
import { startBufferingHostMessages } from './utils/hostHandshake';
import { UnsavedSessionProvider } from './contexts/UnsavedSessionContext';
// Open Sans self-hosteado (no Google Fonts): en instalaciones on-prem sin salida a
// internet, un <link> a fonts.googleapis.com no carga y cae al fallback del navegador.
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/400-italic.css';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';

// refetchOnWindowFocus:false porque muchas queries disparan SQL pesado contra la BD del
// cliente; no queremos re-ejecutarlas solo porque el usuario volvio a la pestaña del navegador.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Antes de React: el padre manda HOST_INIT poco despues del load del iframe y ese
// mensaje no se puede perder (sin el, el padre nunca apaga su loader).
startBufferingHostMessages();

// Base URL para llamadas API
if (process.env.REACT_APP_API_URL) {
  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
} else if (typeof window !== 'undefined') {
  // Tras nginx: app en /{customerId}, API en /{customerId}/api/...
  // Rutas como /embed o /{customerId}/embed no deben usar "embed" como tenant.
  const parts = window.location.pathname.split('/').filter(Boolean);
  let basePath = '';
  if (parts.length >= 2 && parts[1] === 'embed') {
    basePath = `/${parts[0]}`;
  } else if (parts.length >= 1 && parts[0] !== 'embed') {
    basePath = `/${parts[0]}`;
  }
  if (basePath) {
    axios.defaults.baseURL = basePath;
  }
}

// Tema base (previo a que App.js monte su propio ThemeProvider mas especifico con
// vsaasTheme + siteTheme del cliente): mismo origen de marca, para que no haya un
// parpadeo con la paleta generica de MUI antes de la primera carga.
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: vsaasTheme.primary,
    secondary: vsaasTheme.secondary,
    background: vsaasTheme.background,
    text: vsaasTheme.text,
  },
  typography: vsaasTheme.typography,
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NotificationProvider>
          <UnsavedSessionProvider>
            <App />
          </UnsavedSessionProvider>
        </NotificationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
